import { useContext, useEffect } from "react"
import { InAppReview } from "@capacitor-community/in-app-review"
import { VibratoClientContext } from "./contexts"
import { settings } from "@/settings"
import mixpanel from "mixpanel-browser"
import { VibratoClient } from "@/lib/vibrato-api"

interface ReviewPromptProps {
  userSession: any
}

const minCalls = 5
const delaySeconds = 10
const daysToWait = 30

export default function ReviewPrompt({ userSession }: ReviewPromptProps): null {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)

  // Only run for native apps
  if (!settings.IS_NATIVE_APP) {
    return null
  }

  useEffect(() => {
    if (!userSession || !vibratoClient) {
      return
    }

    const checkAndTriggerReview = async () => {
      try {
        // Get the user's plan data which contains total_calls
        const plan = await vibratoClient.getPlan()

        // Check if user has made more than min calls
        if (plan.total_calls > minCalls) {
          // Check if we've already prompted this user for a review
          const reviewPromptedKey = `vibrato_review_prompted_${userSession.id}`
          const hasBeenPrompted = localStorage.getItem(reviewPromptedKey)

          // Check if enough time has passed since last prompt
          const lastPromptKey = `vibrato_last_review_prompt_${userSession.id}`
          const lastPromptTime = localStorage.getItem(lastPromptKey)
          const daysAgo = Date.now() - daysToWait * 24 * 60 * 60 * 1000

          const shouldPrompt = !hasBeenPrompted || (lastPromptTime && parseInt(lastPromptTime) < daysAgo)

          if (shouldPrompt) {
            // Trigger the review automatically after delay
            setTimeout(async () => {
              try {
                await InAppReview.requestReview()

                // Track the review request
                mixpanel.track("Review Requested", {
                  total_calls: plan.total_calls,
                  user_plan: userSession.plan,
                  platform: settings.IS_ANDROID ? "android" : "ios"
                })

                // Mark that we've prompted this user
                localStorage.setItem(reviewPromptedKey, "true")
                localStorage.setItem(lastPromptKey, Date.now().toString())
              } catch (error) {
                console.error("Error in delayed review trigger:", error)
                mixpanel.track("Review Request Error", {
                  error: error instanceof Error ? error.message : String(error),
                  user_plan: userSession.plan
                })
              }
            }, delaySeconds * 1000)
          }
        }
      } catch (error) {
        console.error("Error checking review eligibility:", error)
      }
    }

    checkAndTriggerReview()
  }, [userSession, vibratoClient])

  return null
}
